import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { ROLES } from "../../config/roles";
import { agentService, playerService } from "../../services/api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// Modal for handling representation requests
const RepresentationRequestsModal = ({
  requests,
  onClose,
  onRequestAction,
}) => {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState({});

  const handleDismiss = async (requestId) => {
    setProcessing((prev) => ({ ...prev, [requestId]: true }));
    try {
      const response = await playerService.dismissRepresentationRequest(
        requestId
      );
      if (response.success) {
        toast.success("Request dismissed successfully");
        onRequestAction(requestId, "dismissed");
      } else {
        toast.error(response.error || "Failed to dismiss request");
      }
    } catch (error) {
      console.error("Error dismissing request:", error);
      toast.error("Failed to dismiss request. Please try again.");
    } finally {
      setProcessing((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const handleSendMandate = async (request) => {
    setProcessing((prev) => ({ ...prev, [request.id]: true }));
    try {
      // Navigate to player profile with representation request state
      navigate(`/search/player/${request.wyscout_id || request.sportmonks_id}`, {
        state: { representationRequest: request },
      });
      onRequestAction(request.id, "navigated");
    } catch (error) {
      console.error("Error navigating to player profile:", error);
      toast.error("Failed to navigate to player profile. Please try again.");
    } finally {
      setProcessing((prev) => ({ ...prev, [request.id]: false }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Player Representation Requests
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {requests.map((request, index) => (
            <div
              key={request.id}
              className="flex items-center justify-between px-6 py-3 border border-[#F7D9A4] bg-[#FFF8EC] rounded-xl shadow-lg"
              style={{
                width: "100%",
                height: "64px",
                boxShadow: "0px 4px 16px rgba(16, 11, 39, 0.08)",
              }}
            >
              <div className="flex items-center gap-2">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    width="24"
                    height="24"
                    rx="6"
                    fill="url(#paint0_linear_2130_56168)"
                  />
                  <path
                    d="M12 9V14"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <circle cx="12" cy="17" r="1" fill="white" />
                  <defs>
                    <linearGradient
                      id="paint0_linear_2130_56168"
                      x1="12"
                      y1="0"
                      x2="12"
                      y2="24"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#DBA948" />
                      <stop offset="0.0001" stopColor="#FFC46B" />l
                      <stop offset="1" stopColor="#FFA318" />
                    </linearGradient>
                  </defs>
                </svg>

                <span className="text-sm font-medium">
                  {/* request.email is the AGENT's email (recipient) — wrong to show.
                      Prefer the player's display name, then their email, then a fallback. */}
                  Player "{request.player_name || request.player_email || "Unknown Player"}" has requested
                  representation.
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleSendMandate(request)}
                  disabled={processing[request.id]}
                  className="bg-[#FFB039] px-4 py-2 rounded-xl flex items-center gap-2 cursor-pointer transition-colors disabled:opacity-50"
                >
                  <span className="text-white font-bold text-sm">
                    {processing[request.id] ? "..." : "Review player profile"}
                  </span>
                </button>
                <button
                  onClick={() => handleDismiss(request.id)}
                  disabled={processing[request.id]}
                  className="bg-gray-500 px-4 py-2 rounded-xl flex items-center gap-2 cursor-pointer transition-colors disabled:opacity-50"
                >
                  <span className="text-white font-bold text-sm">
                    {processing[request.id] ? "..." : "Dismiss"}
                  </span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AgentDashboard = ({ user, kycVerified }) => {
  const { currentRole } = useSelector((state) => state.auth);
  const [representationRequests, setRepresentationRequests] = useState([]);
  const [showRequestsModal, setShowRequestsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRepresentationRequests = async () => {
      setLoading(true);
      try {
        const response = await agentService.getRepresentationRequests();
        if (response.success) {
          setRepresentationRequests(response.data || []);
        }
      } catch (error) {
        console.error("Error fetching representation requests:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRepresentationRequests();
  }, []);

  // Only show for agents
  if (currentRole !== ROLES.Agent && currentRole !== ROLES.AgencyAdmin) {
    return null;
  }

  const handleRequestAction = (requestId, action) => {
    // Remove the request from the list
    setRepresentationRequests((prev) =>
      prev.filter((req) => req.id !== requestId)
    );

    // If no more requests, close the modal
    if (representationRequests.length <= 1) {
      setShowRequestsModal(false);
    }
  };

  // Show stacked notifications only if there are multiple requests
  const shouldShowStackedNotifications = representationRequests.length > 1;

  return (
    <div className={`mb-8 flex flex-col gap-6 ${kycVerified ? "mt-6" : ""}`}>
      {/* Stacked toasts - only show if multiple requests */}
      {shouldShowStackedNotifications && (
        <div className="relative h-20 mt-32 md:-mt-2">
          {representationRequests.slice(0, 3).map((request, index) => (
            <div
              key={request.id}
              className="absolute flex flex-col md:flex-row items-start md:items-center justify-between p-4 md:px-6 md:py-3 border border-[#F7D9A4] bg-[#FFF8EC] rounded-xl shadow-lg cursor-pointer w-[calc(100%-2rem)] md:w-full"
              style={{
                maxWidth: `${1400 - index * 24}px`,
                height: "auto",
                left: "1rem",
                bottom: `${20 - index * 10}px`,
                zIndex: representationRequests.length - index,
                opacity: index === 0 ? 1 : index === 1 ? 0.7 : 0.5,
                boxShadow: "0px 4px 16px rgba(16, 11, 39, 0.08)",
              }}
              onClick={() => setShowRequestsModal(true)}
            >
              <div className="flex items-center gap-2 mb-2 md:mb-0">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    width="24"
                    height="24"
                    rx="6"
                    fill="url(#paint0_linear_2130_56168)"
                  />
                  <path
                    d="M12 9V14"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <circle cx="12" cy="17" r="1" fill="white" />
                  <defs>
                    <linearGradient
                      id="paint0_linear_2130_56168"
                      x1="12"
                      y1="0"
                      x2="12"
                      y2="24"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#DBA948" />
                      <stop offset="0.0001" stopColor="#FFC46B" />
                      <stop offset="1" stopColor="#FFA318" />
                    </linearGradient>
                  </defs>
                </svg>

                <span className="text-sm md:text-base font-medium">
                  Player "{request.email || "Unknown Player"}" has requested
                  representation.
                </span>
              </div>

              <div
                className="bg-[#FFB039] px-4 py-2 rounded-xl flex items-center justify-center gap-2 cursor-pointer transition-colors w-full md:w-auto"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRequestsModal(true);
                }}
              >
                <span className="text-white font-bold">Review request</span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.33398 6.00016H10.6673M10.6673 6.00016L6.00065 1.3335M10.6673 6.00016L6.00065 10.6668"
                    stroke="white"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Single notification for one request */}
      {representationRequests.length === 1 && (
        <div className="relative h-auto md:h-20 mt-32 md:-mt-2">
          <div
            className="absolute flex flex-col md:flex-row items-start md:items-center justify-between p-4 md:px-6 md:py-3 border border-[#F7D9A4] bg-[#FFF8EC] rounded-xl shadow-lg w-[calc(100%-2rem)] md:w-full"
            style={{
              maxWidth: "1400px",
              height: "auto",
              left: "1rem",
              bottom: "20px",
              boxShadow: "0px 4px 16px rgba(16, 11, 39, 0.08)",
            }}
          >
            <div className="flex items-center gap-2 mb-2 md:mb-0">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  width="24"
                  height="24"
                  rx="6"
                  fill="url(#paint0_linear_2130_56168)"
                />
                <path
                  d="M12 9V14"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <circle cx="12" cy="17" r="1" fill="white" />
                <defs>
                  <linearGradient
                    id="paint0_linear_2130_56168"
                    x1="12"
                    y1="0"
                    x2="12"
                    y2="24"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#DBA948" />
                    <stop offset="0.0001" stopColor="#FFC46B" />
                    <stop offset="1" stopColor="#FFA318" />
                  </linearGradient>
                </defs>
              </svg>

              <span className="text-sm md:text-base font-medium">
                Player "{representationRequests[0].player_name || representationRequests[0].player_email || "Unknown Player"}"
                has requested representation.
              </span>
            </div>

            <div
              className="bg-[#FFB039] px-4 py-2 rounded-xl flex items-center justify-center gap-2 cursor-pointer transition-colors w-full md:w-auto"
              onClick={() =>
                navigate(
                  `/search/player/${representationRequests[0].wyscout_id || representationRequests[0].sportmonks_id}`,
                  {
                    state: {
                      representationRequest: representationRequests[0],
                    },
                  }
                )
              }
            >
              <span className="text-white font-bold">
                Review player profile
              </span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.33398 6.00016H10.6673M10.6673 6.00016L6.00065 1.3335M10.6673 6.00016L6.00065 10.6668"
                  stroke="white"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* Representation Requests Modal */}
      {showRequestsModal && (
        <RepresentationRequestsModal
          requests={representationRequests}
          onClose={() => setShowRequestsModal(false)}
          onRequestAction={handleRequestAction}
        />
      )}
    </div>
  );
};

export default AgentDashboard;
